
/**
 * User Utility Functions
 */

/**
 * Identifies if an ID is a system or group identifier (not a specific person).
 * @param {string} id - The ID to check.
 * @returns {boolean} True if it's a system ID.
 */
export const isSystemID = (id) => {
    if (!id || typeof id !== 'string') return false;
    // COORDINATOR_DIV_APPROVAL pattern
    if (id.startsWith('COORDINATOR_') && id.endsWith('_APPROVAL')) return true;
    // Add other system IDs here if needed
    const systemIds = ['DG_OFFICE', 'ADMIN', 'COORDINATOR_RIS_APPROVAL'];
    if (systemIds.includes(id)) return true;
    return false;
};

/**
 * Resolves a user's display name from a user object or ID.
 * Handles various user object structures (flat, Cognito Attributes, etc.).
 * 
 * @param {string} userId - The ID to search for.
 * @param {Array} users - The array of user objects.
 * @returns {string} The resolved name or the userId if not found.
 */
export const getUserName = (userId, users = []) => {
    if (!userId) return 'Unassigned';

    // Handle system IDs
    if (isSystemID(userId)) {
        if (userId.endsWith('_APPROVAL')) return 'Pending Approval';
        return userId.replace(/_/g, ' ');
    }

    if (!users || users.length === 0) return userId;

    // Normalize ID for comparison
    const targetId = userId.toLowerCase();

    const user = users.find(u =>
        (u.sub && u.sub.toLowerCase() === targetId) ||
        (u.Username && u.Username.toLowerCase() === targetId) ||
        (u.UserId && u.UserId.toLowerCase() === targetId) ||
        (u.id && u.id.toLowerCase() === targetId)
    );

    if (user) {
        // 1. Try generic Name property
        if (user.Name) return user.Name;

        // 2. Try Cognito Attributes
        if (user.Attributes) {
            const nameAttr = user.Attributes.find(a => a.Name === 'name' || a.Name === 'custom:name');
            if (nameAttr) return nameAttr.Value;

            const emailAttr = user.Attributes.find(a => a.Name === 'email');
            if (emailAttr) return emailAttr.Value;
        }

        // 3. Fallbacks
        return user.Username || user.Email || userId;
    }

    return userId;
};

/**
 * Resolves a user's division from a user ID.
 * @param {string} userId 
 * @param {Array} users 
 * @returns {string|null} The division or null.
 */
export const getUserDivision = (userId, users = []) => {
    if (!userId || isSystemID(userId)) return null;
    const targetId = userId.toLowerCase();
    const user = users.find(u =>
        (u.sub && u.sub.toLowerCase() === targetId) ||
        (u.Username && u.Username.toLowerCase() === targetId) ||
        (u.UserId && u.UserId.toLowerCase() === targetId)
    );
    if (!user) return null;
    return user.Division || user.Attributes?.find(a => a.Name === 'custom:division')?.Value || null;
};
